import { HoldingsQuote } from '@benzinga/quotes-v3-holdings-manager';
import { Quote } from '@benzinga/quotes-v3-manager';
import { DEFAULT_SCANNER_CONFIG, ScannerConfig } from '@benzinga/scanner-config-manager';
import { ScannerManager } from '@benzinga/scanner-manager';
import { SessionContext } from '@benzinga/session-context';
import React from 'react';

export const useBatchQuotes = (symbols: string[], fields: Set<keyof Quote>, config?: Partial<ScannerConfig>) => {
  const [quotes, setQuotes] = React.useState<HoldingsQuote[] | undefined>(undefined);
  const [hasInitialQuotesLoaded, setHasInitialQuotesLoaded] = React.useState(false);

  const memoizedSymbols = React.useMemo(() => symbols, [symbols]);
  const memoizedFields = React.useMemo(() => fields, [fields]);
  const memoizedConfig = React.useMemo(() => config, [config]);

  const symbolsKey = React.useMemo(() => JSON.stringify([...symbols].sort()), [symbols]);

  React.useEffect(() => {
    setQuotes(undefined);
    setHasInitialQuotesLoaded(false);
  }, [symbolsKey]);

  const handleQuotes = React.useCallback((quotes: HoldingsQuote[]) => {
    setQuotes(quotes);
    setHasInitialQuotesLoaded(true);
  }, []);

  useBatchQuotesCallback(memoizedSymbols, memoizedFields, handleQuotes, hasInitialQuotesLoaded, memoizedConfig);

  return quotes;
};

export const useBatchQuotesCallback = (
  symbols: string[],
  fields: Set<keyof Quote>,
  callback: (quotes: HoldingsQuote[]) => void,
  hasInitialQuotesLoaded = false,
  config?: Partial<ScannerConfig>,
) => {
  const session = React.useContext(SessionContext);
  const refreshMs = (config?.refreshInterval ?? 0) * 1000;
  const lastFetchTimeRef = React.useRef<number>(0);
  const lastConfigKey = React.useRef<string>('');

  const limit = 700;

  const fetchQuotes = React.useCallback(
    async (startIndex, endIndex) => {
      const quotes = await session.getManager(ScannerManager).getInstruments({
        ...DEFAULT_SCANNER_CONFIG,
        filters: [
          {
            field: 'symbol',
            operator: 'in',
            parameters: symbols?.slice(startIndex, endIndex) ?? [],
          },
        ],
        limit,
        tableParameters: {
          ...DEFAULT_SCANNER_CONFIG.tableParameters,
          columns: [...Array.from(fields)].map(c => ({ colId: c })),
        },
        ...config,
      });

      if (quotes.err) return;
      return quotes.ok.instruments;
    },
    [session, symbols, fields, config],
  );

  const InitialQuoteFetch = React.useCallback(async () => {
    if (!symbols?.length || fields.size === 0) return;

    const configKey = JSON.stringify({
      config,
      fields: Array.from(fields),
      symbols,
    });

    const now = Date.now();
    const isSameInputs = lastConfigKey.current === configKey;

    if (isSameInputs && refreshMs > 0 && now - lastFetchTimeRef.current < refreshMs) {
      return;
    }
    lastConfigKey.current = configKey;

    const batchedQuotes: HoldingsQuote[] = [];
    for (let i = 0; i < symbols.length; i += limit) {
      const quotes = await fetchQuotes(i, i + limit);
      if (quotes) {
        batchedQuotes.push(...(quotes as HoldingsQuote[]));
      }
    }

    if (batchedQuotes.length > 0) {
      lastFetchTimeRef.current = Date.now();
      callback(batchedQuotes);
    }
  }, [symbols, config, fields, refreshMs, fetchQuotes, callback]);

  React.useEffect(() => {
    if (!hasInitialQuotesLoaded) {
      InitialQuoteFetch();
    }
  }, [InitialQuoteFetch, hasInitialQuotesLoaded]);

  React.useEffect(() => {
    if (!hasInitialQuotesLoaded || !config?.refreshInterval) return;
    InitialQuoteFetch();
    const interval = setInterval(() => {
      InitialQuoteFetch();
    }, config.refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [hasInitialQuotesLoaded, config?.refreshInterval, InitialQuoteFetch]);
};
